import './ViewDialog.scss';

import {
  faCalendar,
  faHome,
  faIdCard,
  faMale,
  faMobileAlt,
  faPhoneAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';

import useMediaQuery from '../../../../../hooks/useMediaQuery';
import { HubType } from '../../../../../types/api/hubs';

type Props = {
  visible: boolean;
  onHide: () => void;
  onEdit?: () => void;
  data: HubType | any;
  isLoading: boolean;
};

function ViewDialog({ visible, data, isLoading, onEdit, onHide }: Props) {
  const { t } = useTranslation();

  const address = `${data?.address.rawName ?? ''} ${
    data?.address.entrance ?? ''
  } ${data?.address.apartment ?? ''}, ${data?.address.placeName ?? ''}`;

  const dialogFooter = !isLoading && (
    <>
      {onEdit && (
        <Button label={t('Edit')} className="p-button-text" onClick={onEdit} />
      )}
      <Button label={t('Close')} onClick={() => onHide()} />
    </>
  );

  const isOnMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={data?.name}
      footer={dialogFooter}
      dismissableMask
      blockScroll
      resizable={false}
      maximizable={isOnMobile}
      maximized={isOnMobile}
      className="hubs-view-dialog"
    >
      <>
        <span className="section-title">{t('General information')}</span>
        <div className="quick-info-container">
          <div className="quick-info">
            <Tooltip target=".name" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                className="name"
                data-pr-tooltip={t('Name')}
                icon={faIdCard}
              />
              <span className="name" data-pr-tooltip={t('Name')}>
                {data?.name}
              </span>
            </div>

            <Tooltip target=".address" mouseTrack mouseTrackLeft={10} />
            <div style={{ display: 'flex' }}>
              <FontAwesomeIcon
                icon={faHome}
                className="address"
                data-pr-tooltip={t('Address')}
              />

              <span className="address" data-pr-tooltip={t('Address')}>
                {address}
              </span>
            </div>

            <Tooltip target=".mobile" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faMobileAlt}
                className="mobile"
                data-pr-tooltip={t('Mobile')}
              />

              <span className="mobile" data-pr-tooltip={t('Mobile')}>
                {data?.mobile || '-'}
              </span>
            </div>

            <Tooltip target=".phone" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faPhoneAlt}
                className="phone"
                data-pr-tooltip={t('Phone')}
              />

              <span className="phone" data-pr-tooltip={t('Phone')}>
                {data?.phone || '-'}
              </span>
            </div>
          </div>

          <div className="quick-info">
            <Tooltip target=".created-at" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faMale}
                className="created-at"
                data-pr-tooltip={t('Created By')}
              />

              <span className="created-by" data-pr-tooltip={t('Created By')}>
                {data?.createdByName || '-'}
              </span>
            </div>
            <Tooltip target=".created-by" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faCalendar}
                className="created-at"
                data-pr-tooltip={t('Created At')}
              />

              <span className="created-at" data-pr-tooltip={t('Created At')}>
                {data?.createdAt || '-'}
              </span>
            </div>
            <Tooltip target=".modified-by" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faMale}
                className="modified-by"
                data-pr-tooltip={t('Modified By')}
              />

              <span className="modified-by" data-pr-tooltip={t('Modified By')}>
                {data?.modifiedByName || '-'}
              </span>
            </div>
            <div>
              <FontAwesomeIcon
                icon={faCalendar}
                className="modified-at"
                data-pr-tooltip={t('Modified At')}
              />

              <span className="modified-at" data-pr-tooltip={t('Modified At')}>
                {data?.modifiedAt || '-'}
              </span>
            </div>
          </div>
        </div>
        <h4>{t('Allowances')}</h4>
        <table cellPadding="0" cellSpacing="0">
          <tbody>
            <tr>
              <td>
                <b>{t('Tax number')}</b>
              </td>
              <td>{data?.taxNumber ?? '-'}</td>
            </tr>
            <tr>
              <td>
                <b>{t('SS number')}</b>
              </td>
              <td>{data?.ssNumber ?? '-'}</td>
            </tr>
            <tr>
              <td>
                <b>{t('Incoming Cash Percent')}</b>
              </td>
              <td>
                {data?.incomingCashPercent
                  ? `${data?.incomingCashPercent} %`
                  : '-'}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('Incoming Invoice Percent')}</b>
              </td>
              <td>
                {data?.incomingInvoicePercent
                  ? `${data?.incomingInvoicePercent} %`
                  : '-'}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('International Cash Percent')}</b>
              </td>
              <td>
                {data?.internationalCashPercent
                  ? `${data?.internationalCashPercent} %`
                  : '-'}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('International Invoice Percent')}</b>
              </td>
              <td>
                {data?.internationalInvoicePercent
                  ? `${data?.internationalInvoicePercent} %`
                  : '-'}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('Outgoing Cash Percent')}</b>
              </td>
              <td>
                {data?.outgoingCashPercent
                  ? `${data?.outgoingCashPercent} %`
                  : '-'}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('Outgoing Invoice Percent')}</b>
              </td>
              <td>
                {data?.outgoingInvoicePercent
                  ? `${data?.outgoingInvoicePercent} %`
                  : '-'}
              </td>
            </tr>
          </tbody>
        </table>
      </>
    </Dialog>
  );
}

export default ViewDialog;
